/**
 * Formatting for the AI operations screen.
 *
 * Money and token counts both need their own rules here: a cost that may be a
 * floor has to say so, and raw token counts are unreadable at the scale this
 * screen deals in.
 */

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * A dollar figure that admits when it is incomplete.
 *
 * OpenRouter prices most calls and not all of them, so a window's total is a
 * floor. "≥ $18.42" is the honest rendering; "$18.42" over a partial sum would
 * be a number that looks exact and is short by an unknown amount. Null is not
 * zero — it means nothing in scope reported a price at all.
 */
export function formatUsd(value: number | null, isPartial = false): string {
  // Not "$0.00": nothing in scope reported a price, which is not the same as
  // the calls having been free. `isPartial` adds nothing to that.
  if (value === null) return 'not reported';

  // Sub-cent totals round to $0.00, which reads as free rather than as small.
  // The `<` already says "small and imprecise", so the floor marker would only
  // stack up as `≥ < $0.01`.
  if (value > 0 && value < 0.005) return '< $0.01';

  return isPartial ? `≥ ${USD.format(value)}` : USD.format(value);
}

const USD_PRECISE = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

/**
 * A per-call average, which is usually a fraction of a cent.
 *
 * Two decimals render nearly every row as "< $0.01", and a column where every
 * row reads the same compares nothing. Four decimals below a dollar keeps
 * "$0.0042" and "$0.0180" apart. Null is an em dash: no call reported a cost
 * to average.
 */
export function formatUsdPrecise(value: number | null): string {
  if (value === null) return '—';
  if (value > 0 && value < 0.00005) return '< $0.0001';
  return value < 1 ? USD_PRECISE.format(value) : USD.format(value);
}

/**
 * Token counts at the scale this screen works in.
 *
 * A month of coach chat is eight or nine digits, and comparing those between
 * rows is something nobody does accurately. Precision below the leading three
 * digits is not information here.
 */
export function formatTokens(value: number): string {
  if (value < 1_000) return String(value);
  if (value < 1_000_000) return `${(value / 1_000).toFixed(1)}k`;
  return `${(value / 1_000_000).toFixed(2)}M`;
}

/**
 * Share of prompt tokens the provider served from its own cache.
 *
 * The one number that says whether the cache breakpoint in `toSystemMessage`
 * is doing anything: a prefix below the provider's minimum caches as a silent
 * no-op, and a flat zero here is the only visible symptom.
 */
export function cacheHitRate(cachedInputTokens: number, inputTokens: number): number | null {
  if (inputTokens <= 0) return null;
  return cachedInputTokens / inputTokens;
}

/**
 * A cache rate as a percentage, or an em dash when there was nothing to cache.
 *
 * Separate from `cacheHitRate` so the number stays a number for anything that
 * wants to compare it, and only the rendering is shared.
 */
export function formatRate(rate: number | null): string {
  return rate === null ? '—' : `${String(Math.round(rate * 100))}%`;
}
